'use client'

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useBootPhaseManager } from '@/hooks/useBootPhaseManager'
import { BootPhaseExecutor, BootState } from '@/components/boot/BootPhaseExecutor'
import { BootLoaderUI } from '@/components/boot/BootLoaderUI'
import { logger } from '@/lib/logger'

interface BootLoaderProps {
  onComplete: (bootState: BootState) => void
  onError: (error: string) => void
}

const BootLoader: React.FC<BootLoaderProps> = ({ onComplete, onError }) => {
  // Define initial phases - VISUALLY IMPRESSIVE loading experience
  const initialPhases = [
    {
      id: 'browser_check',
      name: 'Browser Compatibility',
      description: 'Checking audio capabilities and browser support',
      icon: '🌐',
      duration: 800, // VISUAL
      progress: 0,
      status: 'pending' as const
    },
    {
      id: 'audio_context',
      name: 'Audio Engine Core',
      description: 'Initializing Web Audio API and audio context',
      icon: '🎵',
      duration: 1200, // VISUAL
      progress: 0,
      status: 'pending' as const
    },
    {
      id: 'audio_worklet',
      name: 'Real-time Processor',
      description: 'Loading AudioWorklet for advanced processing',
      icon: '⚡',
      duration: 1500, // VISUAL
      progress: 0,
      status: 'pending' as const
    },
    {
      id: 'neural_engine',
      name: 'Neural Synthesis Engine',
      description: 'Initializing AI neural networks and algorithms',
      icon: '🧠',
      duration: 1800, // VISUAL
      progress: 0,
      status: 'pending' as const
    },
    {
      id: 'component_system',
      name: 'Component Integration',
      description: 'Loading synthesis panels and visualizers',
      icon: '🎨',
      duration: 1000, // VISUAL
      progress: 0,
      status: 'pending' as const
    },
    {
      id: 'performance_monitor',
      name: 'Performance Optimization',
      description: 'Setting up monitoring and optimization systems',
      icon: '📊',
      duration: 600, // VISUAL
      progress: 0,
      status: 'pending' as const
    }
  ]

  // Use the boot phase manager hook
  const {
    phases,
    bootMetrics,
    updatePhaseProgress,
    animateProgress,
    retryPhase,
    addError,
    addWarning,
    completePhase,
    updateCurrentPhase
  } = useBootPhaseManager(initialPhases)

  // Component state
  const [isBooting, setIsBooting] = useState(true)
  const [hasBootStarted, setHasBootStarted] = useState(false)
  const [bootState, setBootState] = useState<BootState>({
    audioContext: null,
    audioWorkletSupported: false,
    scriptProcessorSupported: false,
    neuralEngineReady: false,
    componentsLoaded: false,
    autoResumeAttempted: false,
    autoResumeSuccessful: false
  })

  // Audio element ref for boot sound
  const bootAudioRef = useRef<HTMLAudioElement>(null)

  // Phase executor instance - use ref to prevent recreation on every render
  const phaseExecutorRef = useRef<BootPhaseExecutor>()
  if (!phaseExecutorRef.current) {
    phaseExecutorRef.current = new BootPhaseExecutor()
  }
  const phaseExecutor = phaseExecutorRef.current

  // Auto-play boot audio when ready - no user interaction required
  const playBootAudio = useCallback(async () => {
    if (!bootAudioRef.current || !bootState.autoResumeSuccessful) return

    try {
      logger.log('🎵 Auto-playing boot audio...')
      bootAudioRef.current.currentTime = 0
      await bootAudioRef.current.play()
      logger.log('✅ Boot audio playing successfully!')
    } catch (audioError) {
      logger.warn('Boot audio playback failed:', audioError)
    }
  }, [bootState.autoResumeSuccessful])

  // Auto-play boot audio when audio context is ready
  useEffect(() => {
    if (bootState.autoResumeSuccessful && isBooting) {
      playBootAudio()
    }
  }, [bootState.autoResumeSuccessful, isBooting, playBootAudio])

  // Execute boot sequence
  const runBootSequence = useCallback(async () => {
    try {
      logger.log('🚀 Boot sequence started')

      // Phase 1: Browser compatibility
      logger.log('📋 Starting Phase 1: Browser compatibility')
      updateCurrentPhase('browser_check')
      await phaseExecutor.checkBrowserCompatibility({
        onPhaseComplete: (phaseId) => completePhase(phaseId),
        onPhaseError: (phaseId, error) => addError(`Phase ${phaseId}: ${error}`),
        onStateUpdate: (updates) => setBootState(prev => ({ ...prev, ...updates })),
        animateProgress
      })

      // Phase 2: Audio context
      logger.log('📋 Starting Phase 2: Audio context')
      updateCurrentPhase('audio_context')
      await phaseExecutor.initializeAudioContext({
        onPhaseComplete: (phaseId) => completePhase(phaseId),
        onPhaseError: (phaseId, error) => addError(`Phase ${phaseId}: ${error}`),
        onStateUpdate: (updates) => setBootState(prev => ({ ...prev, ...updates })),
        animateProgress
      })

      // Phase 3: AudioWorklet
      logger.log('📋 Starting Phase 3: AudioWorklet')
      updateCurrentPhase('audio_worklet')
      await phaseExecutor.loadAudioWorklet({
        onPhaseComplete: (phaseId) => completePhase(phaseId),
        onPhaseError: (phaseId, error) => addWarning(`Phase ${phaseId}: ${error}`),
        onStateUpdate: (updates) => setBootState(prev => ({ ...prev, ...updates })),
        animateProgress
      })

      // Phase 4: Neural engine
      logger.log('📋 Starting Phase 4: Neural engine')
      updateCurrentPhase('neural_engine')
      await phaseExecutor.initializeNeuralEngine({
        onPhaseComplete: (phaseId) => completePhase(phaseId),
        onPhaseError: (phaseId, error) => addWarning(`Phase ${phaseId}: ${error}`),
        onStateUpdate: (updates) => setBootState(prev => ({ ...prev, ...updates })),
        animateProgress
      })

      // Phase 5: Component system
      logger.log('📋 Starting Phase 5: Component system')
      updateCurrentPhase('component_system')
      await phaseExecutor.loadComponentSystem({
        onPhaseComplete: (phaseId) => completePhase(phaseId),
        onPhaseError: (phaseId, error) => addError(`Phase ${phaseId}: ${error}`),
        onStateUpdate: (updates) => setBootState(prev => ({ ...prev, ...updates })),
        animateProgress
      })

      // Phase 6: Performance monitor
      logger.log('📋 Starting Phase 6: Performance monitor')
      updateCurrentPhase('performance_monitor')
      await phaseExecutor.initializePerformanceMonitor({
        onPhaseComplete: (phaseId) => completePhase(phaseId),
        onPhaseError: (phaseId, error) => addError(`Phase ${phaseId}: ${error}`),
        onStateUpdate: (updates) => setBootState(prev => ({ ...prev, ...updates })),
        animateProgress
      })

      // Boot complete - system may already be fully operational
      setIsBooting(false)
      logger.log('Boot sequence completed successfully')
      if (bootState.autoResumeSuccessful) {
        logger.log('🎉 System is fully operational - no user interaction required!')
      } else {
        logger.log('⚠️ Audio requires user interaction to start')
      }
      onComplete(bootState)

    } catch (error) {
      logger.error('Boot sequence failed:', error)
      addError(error instanceof Error ? error.message : 'Unknown boot error')
      setIsBooting(false)
      onError(error instanceof Error ? error.message : 'Boot sequence failed')
    }
  }, [completePhase, addError, addWarning, setBootState, animateProgress, setIsBooting, onComplete, onError, updateCurrentPhase])

  useEffect(() => {
    if (!hasBootStarted) {
      setHasBootStarted(true)
      runBootSequence()
    }
  }, [runBootSequence, hasBootStarted])

  // Render the boot loader UI
  return (
    <BootLoaderUI
      phases={phases}
      bootMetrics={bootMetrics}
      isBooting={isBooting}
      audioContextActive={bootState.autoResumeSuccessful}
      bootAudioRef={bootAudioRef}
    />
  )
}

export default BootLoader

